import type { JSX } from 'react';
import { useEffect, useRef, useCallback } from 'react';
import { useTyped } from '@hooks/useTyped';
import { useT } from '@/i18n';
import { useCounter } from '@hooks/useCounter';

import HeroPhoto from './HeroPhoto';
import QuitButton from '@components/sections/QuitButton';
import './Hero.css';

interface CursorState {
  x: number;
  y: number;
  raf: number | null;
}

export default function Hero(): JSX.Element {
  const t = useT();
  const typed = useTyped(t.hero.typed);
  const [yearsCount, yearsRef] = useCounter<HTMLSpanElement>(14);

  const cursorRef  = useRef<HTMLDivElement>(null);
  const stateRef   = useRef<CursorState>({ x: 0, y: 0, raf: null });
  const heroRef    = useRef<HTMLElement>(null);

  const moveCursor = useCallback((e: MouseEvent): void => {
    const state = stateRef.current;
    state.x = e.clientX;
    state.y = e.clientY;
    if (state.raf === null) {
      state.raf = requestAnimationFrame(() => {
        if (cursorRef.current) {
          cursorRef.current.style.transform =
            `translate(${String(state.x)}px, ${String(state.y)}px)`;
        }
        state.raf = null;
      });
    }
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    const cursor = cursorRef.current;
    if (!hero || !cursor) return;
    const show = (): void => { cursor.classList.add('Hero__cursor--visible'); };
    const hide = (): void => { cursor.classList.remove('Hero__cursor--visible'); };
    hero.addEventListener('mouseenter', show);
    hero.addEventListener('mouseleave', hide);
    hero.addEventListener('mousemove', moveCursor);
    return () => {
      hero.removeEventListener('mouseenter', show);
      hero.removeEventListener('mouseleave', hide);
      hero.removeEventListener('mousemove', moveCursor);
    };
  }, [moveCursor]);

  return (
    <section id="hero" className="Hero" ref={heroRef}>
      <div className="Hero__cursor" ref={cursorRef} aria-hidden="true" />
      <div className="Hero__grid" aria-hidden="true" />

      <div className="Hero__content">
        <div className="Hero__left">
          <p className="Hero__eyebrow">
            <span className="Hero__dot" aria-hidden="true" />
            {t.hero.location}
          </p>

          <h1 className="Hero__name">
            Pierre
            <span className="Hero__name-accent">Séré</span>
          </h1>

          <div className="Hero__typed" aria-live="polite">
            <span className="Hero__typed-text">{typed}</span>
            <span className="Hero__cursor-char" aria-hidden="true">|</span>
          </div>

          <p className="Hero__bio">
            {t.hero.bio}
          </p>

          <div className="Hero__stats">
            <div className="Hero__stat">
              <span className="Hero__stat-num" ref={yearsRef}>{yearsCount}</span>
              <span className="Hero__stat-label">{t.hero.yearsLabel}</span>
            </div>
            <div className="Hero__stat-divider" aria-hidden="true" />
            <div className="Hero__stat">
              <span className="Hero__stat-num">{t.hero.reportsValue}</span>
              <span className="Hero__stat-label">{t.hero.reportsLabel}</span>
            </div>
          </div>

          <div className="Hero__actions">
            <a href="#contact" className="btn-primary">{t.hero.ctaContact}</a>
            <a href="#parcours" className="btn-ghost">{t.hero.ctaJourney}</a>
          </div>

          <QuitButton />
        </div>

        <div className="Hero__right">
          <HeroPhoto />
        </div>
      </div>

      <a href="#expertise" className="Hero__scroll" aria-label={t.hero.scrollAria}>
        <span className="Hero__scroll-line" aria-hidden="true" />
        <span className="Hero__scroll-label">{t.hero.scroll}</span>
      </a>
    </section>
  );
}
